import React, { useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Search, Plus, Upload, Download, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AddGuestModal } from './AddGuestModal';
import { ImportGuestsModal } from './ImportGuestsModal';
import { EditEventGuestModal } from './EditEventGuestModal';
import { useToast } from '@/hooks/use-toast';
import { useEventGuests } from '@/hooks/useEventGuests';
import { StatusBadge } from '@/components/status/StatusBadge';

interface EventGuestsTabProps {
  eventId: string;
  eventName: string;
}

export const EventGuestsTab: React.FC<EventGuestsTabProps> = ({ eventId, eventName }) => {
  const [guestSearchTerm, setGuestSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [rsvpFilter, setRsvpFilter] = useState("all");
  const [isImportGuestsModalOpen, setIsImportGuestsModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<any>(null);

  const { toast } = useToast();
  const { data: eventGuests, isLoading: guestsLoading } = useEventGuests(eventId);

  const guestCounts = {
    confirmed: eventGuests?.filter(g => g.rsvp_status === "Confirmed").length || 0,
    pending: eventGuests?.filter(g => g.rsvp_status === "Pending").length || 0,
    invited: eventGuests?.filter(g => g.rsvp_status === "Invited").length || 0,
    declined: eventGuests?.filter(g => g.rsvp_status === "Declined").length || 0,
    suggested: eventGuests?.filter(g => g.rsvp_status === "Suggested").length || 0,
  };

  const filteredGuests = eventGuests?.filter(guest => {
    const matchesSearch = guest.full_name.toLowerCase().includes(guestSearchTerm.toLowerCase()) ||
      (guest.handle && guest.handle.toLowerCase().includes(guestSearchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === "all" || guest.guest_type === categoryFilter;
    const matchesRsvp = rsvpFilter === "all" || guest.rsvp_status === rsvpFilter;
    return matchesSearch && matchesCategory && matchesRsvp;
  }) || [];

  const handleExportGuestList = () => {
    if (!filteredGuests || filteredGuests.length === 0) {
      toast({ title: "Warning", description: "No guests available to export.", variant: "destructive" });
      return;
    }
    try {
      const headers = ['name', 'handle', 'contact_info', 'category', 'rsvp_status', 'notes'];
      const csvData = [
        headers,
        ...filteredGuests.map(guest => [
          guest.full_name || '',
          guest.handle || '',
          guest.email || guest.phone || '',
          guest.guest_type || '',
          guest.rsvp_status || '',
          guest.notes || ''
        ])
      ];
      const csvContent = csvData.map(row =>
        row.map(field => {
          if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
            return '"' + field.replace(/"/g, '""') + '"';
          }
          return field;
        }).join(',')
      ).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const eventSlug = eventName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'event';
        link.setAttribute('download', `guestlist-${eventSlug}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      toast({ title: "Success", description: `Guest list exported successfully (${filteredGuests.length} guests).` });
    } catch (error) {
      console.error('Export error:', error);
      toast({ title: "Error", description: "Failed to export guest list.", variant: "destructive" });
    }
  };

  return (
    <>
      <Card className="bg-white shadow-lg border-0">
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-gray-900">Guest List</CardTitle>
              <p className="text-gray-600 mt-1">Manage event guests and RSVPs</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="flex items-center gap-2" onClick={() => setIsImportGuestsModalOpen(true)}>
                <Upload className="h-4 w-4" />
                Import CSV
              </Button>
              <Button variant="outline" className="flex items-center gap-2" onClick={handleExportGuestList}>
                <Download className="h-4 w-4" />
                Export List
              </Button>
              <AddGuestModal eventId={eventId}>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Guest
                </Button>
              </AddGuestModal>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-green-600">{guestCounts.confirmed}</div>
              <div className="text-sm text-gray-600">Confirmed</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-yellow-600">{guestCounts.pending}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-blue-600">{guestCounts.invited}</div>
              <div className="text-sm text-gray-600">Invited</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-red-600">{guestCounts.declined}</div>
              <div className="text-sm text-gray-600">Declined</div>
            </Card>
            <Card className="text-center p-4">
              <div className="text-2xl font-bold text-gray-600">{guestCounts.suggested}</div>
              <div className="text-sm text-gray-600">Suggested</div>
            </Card>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search guests..."
                value={guestSearchTerm}
                onChange={(e) => setGuestSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Influencer">Influencer</SelectItem>
                <SelectItem value="Media">Media</SelectItem>
                <SelectItem value="VIP">VIP</SelectItem>
                <SelectItem value="Others">Others</SelectItem>
              </SelectContent>
            </Select>
            <Select value={rsvpFilter} onValueChange={setRsvpFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All RSVP Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All RSVP Status</SelectItem>
                <SelectItem value="Confirmed">Confirmed</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Invited">Invited</SelectItem>
                <SelectItem value="Declined">Declined</SelectItem>
                <SelectItem value="Suggested">Suggested</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Guests ({filteredGuests.length})</h3>
            <p className="text-sm text-gray-600 mb-4">Event guest list with RSVP status</p>
          </div>

          {guestsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="medium" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest Name</TableHead>
                  <TableHead>Handle</TableHead>
                  <TableHead>Contact Info</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>RSVP Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGuests.map((guest) => (
                  <TableRow key={guest.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="font-semibold text-gray-900">{guest.full_name}</div>
                    </TableCell>
                    <TableCell className="text-gray-600">{guest.handle || "-"}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-gray-900">{guest.phone}</div>
                        <div className="text-gray-500">{guest.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs bg-gray-50 text-gray-700">
                        {guest.guest_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={guest.rsvp_status} className="text-[11px]" />
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-[200px] truncate">
                      {guest.notes || "-"}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setEditingGuest(guest)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ImportGuestsModal
        isOpen={isImportGuestsModalOpen}
        onClose={() => setIsImportGuestsModalOpen(false)}
        eventId={eventId}
      />

      <EditEventGuestModal
        isOpen={!!editingGuest}
        onClose={() => setEditingGuest(null)}
        eventGuest={editingGuest}
      />
    </>
  );
};
